import { useEffect, useRef, useState, useCallback } from "react";

const CLEAR_TIMEOUT = 1500; // ms of inactivity before clearing filter

/**
 * Typeahead product filter + Tab navigation for POS.
 *
 * - Letter/number/space → builds filter string
 * - Backspace → removes last char
 * - Escape → clears filter + selection
 * - Tab / Shift+Tab → cycle through visible products
 * - Enter → select highlighted product
 * - F1 = En Comedor, F2 = Para Recoger, F3 = A Domicilio, F4 = Plataformas
 * - F5 = Make order (Guardar) — triggers order with current delivery type
 *
 * Auto-clears filter after 1.5s of no typing.
 */
export const usePosFilter = ({ onSelect, onDelivery, onMakeOrder } = {}) => {
  const [filter, setFilter] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const visibleCountRef = useRef(0);
  const timerRef = useRef(null);
  const onSelectRef = useRef(onSelect);
  const onDeliveryRef = useRef(onDelivery);
  const onMakeOrderRef = useRef(onMakeOrder);

  // Keep refs fresh without re-attaching listener
  useEffect(() => {
    onSelectRef.current = onSelect;
  }, [onSelect]);
  useEffect(() => {
    onDeliveryRef.current = onDelivery;
  }, [onDelivery]);
  useEffect(() => {
    onMakeOrderRef.current = onMakeOrder;
  }, [onMakeOrder]);

  const setVisibleCount = useCallback((count) => {
    visibleCountRef.current = count;
  }, []);

  const clearFilter = useCallback(() => {
    setFilter("");
    setSelectedIndex(-1);
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const resetTimer = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      setFilter("");
      setSelectedIndex(-1);
      timerRef.current = null;
    }, CLEAR_TIMEOUT);
  }, []);

  useEffect(() => {
    const deliveryMap = {
      F1: "for_here",
      F2: "pickup",
      F3: "delivery",
      F4: "other_apps",
    };

    function onKeyDown(e) {
      // Skip when a modal is open (Reactstrap adds modal-open to body)
      if (document.body.classList.contains("modal-open")) return;

      const tag = e.target.tagName;
      if (
        tag === "INPUT" ||
        tag === "TEXTAREA" ||
        tag === "SELECT" ||
        e.target.isContentEditable
      ) {
        return;
      }
      if (e.ctrlKey || e.metaKey || e.altKey) return;

      // Delivery shortcuts: F1-F4
      if (deliveryMap[e.key]) {
        e.preventDefault();
        if (onDeliveryRef.current) {
          onDeliveryRef.current(deliveryMap[e.key]);
        }
        return;
      }

      // F5 — Make order / Guardar
      if (e.key === "F5") {
        e.preventDefault();
        if (onMakeOrderRef.current) {
          onMakeOrderRef.current();
        }
        return;
      }

      if (e.key === "Escape") {
        clearFilter();
        return;
      }

      if (e.key === "Backspace") {
        e.preventDefault();
        setFilter((prev) => prev.slice(0, -1));
        setSelectedIndex(-1);
        resetTimer();
        return;
      }

      // Tab / Shift+Tab — cycle through visible products
      if (e.key === "Tab") {
        e.preventDefault();
        const count = visibleCountRef.current;
        if (count === 0) return;
        setSelectedIndex((prev) => {
          if (e.shiftKey) {
            return prev <= 0 ? count - 1 : prev - 1;
          }
          return prev >= count - 1 ? 0 : prev + 1;
        });
        return;
      }

      // Enter — select highlighted product
      if (e.key === "Enter") {
        e.preventDefault();
        if (onSelectRef.current) {
          onSelectRef.current();
        }
        return;
      }

      // Accept letters, numbers, and space
      if (e.key.length === 1 && /[a-zA-Z0-9\s]/.test(e.key)) {
        e.preventDefault();
        setFilter((prev) => prev + e.key);
        setSelectedIndex(-1);
        resetTimer();
      }
    }

    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [clearFilter, resetTimer]);

  return { filter, clearFilter, selectedIndex, setVisibleCount };
};
